import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { UserPlus, Search, Save, X } from 'lucide-react';
import { API } from '../../utils/apiConfig';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export function B2BSmartAssignForm({ orgs, onDone, flash }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignOrgId, setAssignOrgId] = useState('');
  const [assignRole, setAssignRole] = useState('driver');
  const [assignJobTitle, setAssignJobTitle] = useState('');
  const [searching, setSearching] = useState(false);

  const inputStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.3)', background: 'rgba(15,23,42,0.6)', color: '#e2e8f0' };
  const labelStyle = { fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' };

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
      setSearchResults(res.data.users || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, doSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return flash('error', t('errors.validation'));
    try {
      await axios.post(`${API}/admin/b2b-orgs/assign-user`, {
        user_id: selectedUser.id,
        org_id: assignOrgId,
        b2b_role: assignOrgId ? assignRole : '',
        job_title: assignJobTitle,
      }, { headers: getHeaders() });
      flash('success', assignOrgId ? t('b2b.assigned', { org: assignOrgId }) : t('b2b.removed'));
      onDone();
    } catch (err) {
      flash('error', err.response?.data?.detail || t('errors.generic'));
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="b2b-assign-form" style={{
      background: 'rgba(30,41,59,0.85)', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid rgba(99,102,241,0.3)',
    }}>
      <h3 style={{ marginBottom: '16px', color: '#a5b4fc' }}><UserPlus size={18} style={{ marginRight: '8px' }} />{t('b2b.assignUser')}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
        {/* User Search */}
        <div style={{ gridColumn: 'span 2' }}>
          <label style={labelStyle}>{t('b2b.searchUsers')}</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '10px', color: '#64748b' }} />
            <input data-testid="b2b-assign-search" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setSelectedUser(null); }}
              placeholder={t('b2b.searchUsers')} style={{ ...inputStyle, paddingLeft: '30px' }} />
          </div>
          {searching && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{t('common.loading')}</div>}
          {searchResults.length > 0 && !selectedUser && (
            <div style={{ border: '1px solid rgba(148,163,184,0.3)', borderRadius: '6px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto', background: 'rgba(15,23,42,0.9)' }}>
              {searchResults.map(u => (
                <div key={u.id} data-testid={`b2b-assign-user-option-${u.id}`}
                  onClick={() => { setSelectedUser(u); setSearchResults([]); setSearchTerm(`${u.firstName} ${u.lastName} <${u.email}>`); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(148,163,184,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ color: '#e2e8f0' }}>{u.firstName} {u.lastName}</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{u.email}{u.org_id ? ` (${u.org_id})` : ''}</span>
                </div>
              ))}
            </div>
          )}
          {selectedUser && (
            <div style={{ marginTop: '6px', padding: '6px 10px', borderRadius: '6px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
              <span>{selectedUser.firstName} {selectedUser.lastName} — {selectedUser.email}</span>
              <button type="button" onClick={() => { setSelectedUser(null); setSearchTerm(''); }} style={{ background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer' }}><X size={14} /></button>
            </div>
          )}
        </div>

        {/* Org Dropdown */}
        <div>
          <label style={labelStyle}>{t('b2b.selectOrg')}</label>
          <select data-testid="b2b-assign-org-id" value={assignOrgId} onChange={e => setAssignOrgId(e.target.value)} style={inputStyle}>
            <option value="">{t('b2b.removeFromOrg')}</option>
            {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name} ({o.org_id})</option>)}
          </select>
        </div>

        {/* Role */}
        <div>
          <label style={labelStyle}>{t('b2b.role')}</label>
          <select data-testid="b2b-assign-role" value={assignRole} onChange={e => setAssignRole(e.target.value)} style={inputStyle} disabled={!assignOrgId}>
            <option value="driver">{t('b2b.roles.driver')}</option>
            <option value="org_admin">{t('b2b.roles.org_admin')}</option>
            <option value="super_admin">{t('b2b.roles.super_admin')}</option>
          </select>
        </div>

        {/* Job Title */}
        <div>
          <label style={labelStyle}>{t('b2b.jobTitle')}</label>
          <input data-testid="b2b-assign-job-title" value={assignJobTitle} onChange={e => setAssignJobTitle(e.target.value)}
            placeholder={t('b2b.jobTitlePlaceholder')} style={inputStyle} disabled={!assignOrgId} />
        </div>
      </div>
      <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
        <button type="submit" className="admin-action-btn stats" data-testid="b2b-assign-submit" disabled={!selectedUser}><Save size={14} /> {t('b2b.assignUser')}</button>
        <button type="button" className="admin-action-btn debug" onClick={onDone}><X size={14} /> {t('common.cancel')}</button>
      </div>
    </form>
  );
}
